import {
  DEFAULT_LOCKED_DIMENSION,
  type LockedDimension,
} from '../constants/editorDefaults';

export type ItemDimensions = {
  width: number;
  height: number;
  scale: number;
};

export function getLockedDimension(item: { lockedDimension?: LockedDimension }): LockedDimension {
  return item.lockedDimension ?? DEFAULT_LOCKED_DIMENSION;
}

export function computeSizeFromLockedWidth(
  naturalWidth: number,
  naturalHeight: number,
  lockedWidth: number,
  maxHeight?: number
): ItemDimensions {
  if (naturalWidth <= 0 || naturalHeight <= 0) {
    let height = naturalHeight > 0 ? naturalHeight : lockedWidth;
    if (maxHeight && maxHeight > 0) {
      height = Math.min(height, maxHeight);
    }
    return { width: lockedWidth, height, scale: 1 };
  }

  let scale = lockedWidth / naturalWidth;
  if (maxHeight && maxHeight > 0 && naturalHeight * scale > maxHeight) {
    scale = maxHeight / naturalHeight;
  }

  return {
    width: naturalWidth * scale,
    height: naturalHeight * scale,
    scale,
  };
}

export function computeSizeFromLockedHeight(
  naturalWidth: number,
  naturalHeight: number,
  lockedHeight: number,
  maxWidth?: number
): ItemDimensions {
  if (naturalHeight <= 0 || naturalWidth <= 0) {
    let width = naturalWidth > 0 ? naturalWidth : lockedHeight;
    if (maxWidth && maxWidth > 0) {
      width = Math.min(width, maxWidth);
    }
    return { width, height: lockedHeight, scale: 1 };
  }

  let scale = lockedHeight / naturalHeight;
  if (maxWidth && maxWidth > 0 && naturalWidth * scale > maxWidth) {
    scale = maxWidth / naturalWidth;
  }

  return {
    width: naturalWidth * scale,
    height: naturalHeight * scale,
    scale,
  };
}

export type ItemSizeLimits = {
  maxHeight?: number;
  maxWidth?: number;
};

export function computeItemSizeFromLock(
  naturalWidth: number,
  naturalHeight: number,
  lockedDimension: LockedDimension,
  currentWidth: number,
  currentHeight: number,
  limits?: ItemSizeLimits
): ItemDimensions {
  if (lockedDimension === 'width') {
    return computeSizeFromLockedWidth(
      naturalWidth,
      naturalHeight,
      currentWidth,
      limits?.maxHeight
    );
  }
  return computeSizeFromLockedHeight(
    naturalWidth,
    naturalHeight,
    currentHeight,
    limits?.maxWidth
  );
}

export function computeTextItemSizeFromLock(
  item: { lockedDimension?: LockedDimension; width: number; height: number },
  baseWidth: number,
  baseHeight: number,
  limits?: ItemSizeLimits
): ItemDimensions {
  return computeItemSizeFromLock(
    baseWidth,
    baseHeight,
    getLockedDimension(item),
    item.width,
    item.height,
    limits
  );
}

export function computeEditorItemSizeFromLock(item: {
  lockedDimension?: LockedDimension;
  width: number;
  height: number;
  baseWidth: number;
  baseHeight: number;
}): ItemDimensions {
  return computeItemSizeFromLock(
    item.baseWidth,
    item.baseHeight,
    getLockedDimension(item),
    item.width,
    item.height
  );
}
